import {useEffect, useState} from 'react';
import {Container} from 'react-bootstrap';
import {Link, NavigateFunction, useLocation, useNavigate, Location} from 'react-router-dom';
import Classes from './SearchBar.module.scss';
import useWindowSize from '../../hooks/useWindowSize';
import Logo from '../../assets/icons/brandLogo.svg';
import {useQuery} from '@tanstack/react-query';
import {searchFilter, trendingTags} from '../../api/apiClient';
import {useAuth} from '../../context/AuthContext';
import useDebounce from '../../hooks/useDebounce';

//== Icons
import SearchIcon from '../../assets/icons/search.svg';
import SearchIconWhite from '../../assets/icons/searchWhie.svg';


const SearchBar = () => {
    //== Navigate Search page
    const navigate: NavigateFunction = useNavigate();
    const location: Location = useLocation();
    const {width} = useWindowSize();
    const {getToken} = useAuth();
    const token = getToken();

    const [focusedInput, setFocusedInput] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const handleOnChange = (e: any): void => {
        setSearchValue(e.target.value);
        setFocusedInput(true);
    }

    //== Trending SearchTags Api
    let trendingTagContent = null;
    const {data: trendingTagsData, isError: tgError, isLoading: tgLoading} = useQuery(
        ['trendingTags'],
        trendingTags, {
            staleTime: 1440 * 60 * 1000,
            cacheTime: 1440 * 60 * 1000,
        });
    if (!tgLoading && !tgError && trendingTagsData?.data !== '') {
        trendingTagContent = (
            <div className={Classes.searchFilterWrap}>
                {trendingTagsData?.data?.trendingTag[0].trendingTagList.split(',')?.map((trTags: any, i: number) => (
                    <Link
                        key={i}
                        onClick={() => setSearchValue(encodeURIComponent(trTags))}
                        to={`/search/${trTags}`}
                        state={Array(trTags)}
                    >
                        {trTags}
                    </Link>
                ))}
            </div>
        );
    }

    //== Search Filter Query
    const deferredQuery: string = useDebounce(searchValue.trim());
    const {data, isError, isLoading} = useQuery({
        queryKey: ['searchFilter', deferredQuery],
        queryFn: () => searchFilter(deferredQuery, token),
        enabled: !!deferredQuery,
    });

    let content = null;
    if (!isLoading && !isError && data?.data?.tagList[0]?.matchingWord?.length > 0) {
        content = (
            <div className={Classes.searchFilterWrap}>
                {data?.data?.tagList[0]?.matchingWord?.map((tag: any, i: number) => (
                    <Link
                        key={i}
                        to={`/search/${encodeURIComponent(tag)}`}
                        state={Array(tag)}
                    >
                        {tag}
                    </Link>
                ))}
            </div>
        );
    }

    const searchOnSubmit = (e: any) => {
        e.preventDefault();
        navigate(`search/${encodeURIComponent(searchValue)}`, {state: Array(searchValue)});
        setFocusedInput(false);
    }

    useEffect(() => {
        if (location.pathname === `/search/${searchValue}`) {
            setSearchValue(decodeURIComponent(searchValue));
        } else {
            setSearchValue('');
        }
        // eslint-disable-next-line
    }, [location.pathname]);

    //== Add Sticky Class
    useEffect(() => {
        let stickySearch: any = document.querySelector('#searchBar');
        const isSticky = (): void => {
            const scrollTop: number = window.scrollY;
            const scrollTopDevices = ((width <= 767.99) ? 52 : 70);
            if (scrollTop >= scrollTopDevices) {
                stickySearch.classList.add(Classes.isSticky);
            } else {
                stickySearch.classList.remove(Classes.isSticky);
            }
        };

        window.addEventListener("scroll", isSticky);
        return (): void => {
            window.removeEventListener("scroll", isSticky);
        };
        // eslint-disable-next-line
    }, []);

    return (
        <div id={'searchBar'}>
            <Container className={Classes.stickyContainer}>
                <div className="d-flex align-items-center">
                    <div className={Classes.stickyLogo}>
                        <Link
                            to={'/'}
                            className={Classes.navbarBrand}
                            onClick={(): void => {
                                document.documentElement.scrollTo({
                                    top: 0,
                                    left: 0,
                                    // @ts-expect-error
                                    behavior: 'instant',
                                });
                            }}
                        >
                            <img src={Logo} alt="Gifbuz" className={'img-fluid'}/>
                        </Link>
                    </div>
                    <form
                        className={`${Classes.searchForm} d-flex align-items-center`}
                        onSubmit={searchOnSubmit}
                    >
                        <input
                            type="search"
                            className={`${Classes.searchInput} form-control`}
                            placeholder="Search for Gifs and Stickers"
                            value={decodeURIComponent(searchValue)}
                            aria-label="Search for Gifs and Stickers"
                            onChange={handleOnChange}
                            onFocus={() => setFocusedInput(true)}
                            onBlur={() => {
                                setTimeout(() => {
                                    setFocusedInput(false);
                                }, 200);
                            }}
                        />
                        <button
                            type="submit"
                            disabled={!searchValue.trim() || searchValue === '.' || searchValue === '..'}
                        >
                            <img src={width <=576 ? SearchIconWhite : SearchIcon} alt="Search" className={'img-fluid'}/>
                        </button>
                        {focusedInput ? content : null}
                        {(searchValue === '' && focusedInput) ? trendingTagContent : null}
                    </form>
                </div>
            </Container>
        </div>
    );
};

export default SearchBar;